import { memo, useMemo } from "react";

import { map } from "lodash";

import { SelectProps } from "@mui/material";

import { IAppConfig } from "#/features/User/types";
import { IUnit } from "@/api";
import { useFacilityById, useHealthSystems, useUnitById } from "@/common/AppConfigUtils";
import { isOnMobile } from "@/common/utils/isOnMobile";

import { UnitPicker } from "./UnitPicker";
import "./UnitPicker.scss";
import { UnitPickerDeprecated as UnitPickerGrouped } from "./UnitPickerDeprecated";
import { UnitPickerMobile } from "./UnitPickerMobile";

/**
 * UnitPickerWrapper component - Wrapper for UnitPicker and UnitPickerMobile
 * This component is used to wrap the UnitPicker and UnitPickerMobile components
 * and provide a unified interface for selecting units
 * Mobile component will be used if the user is on mobile and the user is part of multiple facilities
 * Desktop component will be used if the user is on desktop or the user is part of a single facility
 */
export const UnitPickerWrapper = memo(
  ({
    onChange,
    values,
    valueIds: valueIdsFromParams,
    showFullUnitNames = false,
    size,
    multiple = true,
    unitOptions,
    homeUnitToShowFloatingIcons,
    disableClickOutsideUnit = false,
    haveAllOption = true,
    unitGroupsAllowed = false,
    reversed = false,
    className = "multi-line",
    showInline = false,
    dropdownClassName,
    label,
  }: {
    onChange: (selectedUnits: IUnit[]) => void; // Callback when selection changes
    values?: IUnit[]; // Selected units (alternative to valueIds)
    valueIds?: IUnit["id"][]; // IDs of selected units (alternative to values)
    showFullUnitNames?: boolean; // Whether to show full unit names in selection display or hierarchy (like market, facility, etc.)
    size?: SelectProps["size"]; // Size of the select component
    multiple?: boolean; // New prop
    unitOptions?: IUnit[] | IAppConfig["accessibleUnits"]; // Scope down the units to choose from
    homeUnitToShowFloatingIcons?: IUnit["id"]; // Unit ID to not show floating icons for, all the other units will show floating icons
    disableClickOutsideUnit?: boolean; // Disable click outside unit to select
    haveAllOption?: boolean; // Whether to show the "All" option
    unitGroupsAllowed?: boolean; // Whether to show the unit group picker when conditions are met
    reversed?: boolean; // Whether to reverse the direction of the unit picker
    className?: string; // Additional class name to apply to the component
    dropdownClassName?: string; // Additional class name to apply to the dropdown component
    showInline?: boolean; // Whether to show the mobile picker
    label?: string; // Label for the unit picker
  }) => {
    // Generate a flat list of all units from health systems, indexed by ID for quick lookup
    const healthSystems = useHealthSystems();
    const facilityById = useFacilityById();
    const unitById = useUnitById();

    const allUnitsMap = useMemo(() => {
      const possibleUnitMap = new Map<string, IUnit>();

      // Extract units from the nested structure
      healthSystems.forEach((healthSystem) => {
        healthSystem.regions?.forEach((region) => {
          if (!region.archivedAt) {
            region.markets?.forEach((market) => {
              if (market.archivedAt) return;

              market.facilityIds?.forEach((facilityId) => {
                const facility = facilityById[facilityId];
                if (!facility || facility.archivedAt) return;

                facility.unitIds?.forEach((unitId) => {
                  const unit = unitById[unitId];
                  if (!unit || unit.archivedAt) return;

                  possibleUnitMap.set(unit.id, unit);
                });
              });
            });
          }
        });
      });

      let finalUnitMap = new Map<string, IUnit>();

      if (unitOptions) {
        unitOptions.forEach((unit) => {
          if (!unit.archivedAt) {
            const possibleUnit = possibleUnitMap.get(unit.id);
            if (possibleUnit) {
              finalUnitMap.set(unit.id, possibleUnit);
            }
          }
        });
      } else {
        finalUnitMap = possibleUnitMap;
      }

      return finalUnitMap;
    }, [facilityById, healthSystems, unitById, unitOptions]);

    // Check if all units belong to a single facility
    const unitsArray = Array.from(allUnitsMap.values());
    const singleFacility =
      unitsArray.length > 0 &&
      unitsArray.every((unit) => unit.facilityId === unitsArray[0]?.facilityId);
    const unitsHaveGroups = unitsArray.some(
      (unit) => !!unit.configuration.settings?.schedulerApp?.unitGroupName,
    );
    // Normalize valueIds from either values prop or valueIds prop
    const valueIds = useMemo(
      () => valueIdsFromParams || map(values, "id"),
      [values, valueIdsFromParams],
    );

    // If the user is on mobile and part of multiple facilities, use the mobile component
    // If the user is on a single facility and at least one unit has groups, use the grouped picker component
    // Otherwise, use the desktop component
    const showMobilePicker = showInline || (!singleFacility && isOnMobile());
    const showGroupedPicker = singleFacility && unitsHaveGroups && unitGroupsAllowed;

    if (showMobilePicker) {
      return (
        <UnitPickerMobile
          onChange={onChange}
          values={values}
          valueIds={valueIds}
          showFullUnitNames={showFullUnitNames}
          size={size}
          multiple={multiple}
          unitOptions={unitOptions}
          homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
          disableClickOutsideUnit={disableClickOutsideUnit}
          haveAllOption={haveAllOption}
          className={className}
        />
      );
    } else if (showGroupedPicker) {
      return (
        //Use deprecated unit picker component for now, later to be fully replaced by data hierarchy
        <UnitPickerGrouped
          size={size}
          unitOptions={unitOptions}
          valueIds={valueIds}
          values={values}
          onChange={onChange}
          showFullUnitNames={showFullUnitNames}
          className={className}
        />
      );
    } else {
      return (
        <UnitPicker
          label={label}
          onChange={onChange}
          values={values}
          valueIds={valueIds}
          showFullUnitNames={showFullUnitNames}
          size={size}
          multiple={multiple}
          unitOptions={unitOptions}
          homeUnitToShowFloatingIcons={homeUnitToShowFloatingIcons}
          disableClickOutsideUnit={disableClickOutsideUnit}
          haveAllOption={haveAllOption}
          reversed={reversed}
          className={className}
          dropdownClassName={dropdownClassName}
        />
      );
    }
  },
);
